// Auth-endpoint client for the sign in / forgot / reset screens.
//
// These calls deliberately use native fetch and NOT the shared apiFetch:
// apiFetch treats any 401 as an expired session and redirects to /login while
// clearing the token. Devise returns 401 on wrong credentials, so routing the
// sign-in through apiFetch would turn a bad password into a redirect loop that
// swallows the error message. Here a non-2xx response is returned to the caller
// to branch on, never thrown.
function getCsrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  );
}

const jsonHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  "X-Requested-With": "XMLHttpRequest",
  "X-CSRF-Token": getCsrfToken(),
});

async function request(path, method, body) {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: jsonHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  return {
    status: response.status,
    data,
    authorization: response.headers.get("Authorization"),
  };
}

export async function signIn({
  email,
  password,
  otpCode,
  backupCode,
  rememberMe,
  rememberMeToken,
  locale,
  isPasskeySupported,
  bypassPasskey,
}) {
  return request("/users/sign_in", "POST", {
    locale,
    is_passkey_supported: isPasskeySupported,
    // "I don't have my passkey anymore" -- skips the server's normal forcing
    // of the passkey ceremony for a user whose preferred method is passkey,
    // even though their (now-inaccessible) credential is still registered.
    bypass_passkey: bypassPasskey,
    user: {
      email,
      password,
      otp_code: otpCode,
      backup_code: backupCode,
      remember_me: rememberMe,
      remember_me_token: rememberMeToken,
    },
  });
}

export async function sendResetPasswordInstructions({ email, locale }) {
  const { data } = await request("/users/password", "POST", { locale, email });
  return data;
}

export async function resetPassword({
  password,
  passwordConfirmation,
  token,
  locale,
}) {
  const { data } = await request("/users/password", "PUT", {
    locale,
    password,
    password_confirmation: passwordConfirmation,
    token,
  });
  return data;
}

export async function signOut() {
  const { data } = await request("/users/sign_out", "DELETE", {});
  return data;
}

// Unauthenticated by design: the reset screen calls this before any session
// exists, so it lives here with the other pre-auth endpoints instead of
// apiFetch. Responds { valid: boolean }.
export async function validateResetToken(token) {
  const { data } = await request(
    `/api/users/validate_reset_token?token=${encodeURIComponent(token)}`,
    "GET"
  );
  return data;
}

// Unauthenticated by design, same reasoning as validateResetToken above: the
// login screen calls this right after the user types their email, before any
// session exists, to decide whether to run the passkey ceremony or fall back
// to the password/OTP flow. See Api::PreferredSignInMethodController#check_passkey.
export async function checkPasskey(email) {
  const { data } = await request(
    `/api/preferred_sign_in_method/check_passkey?email=${encodeURIComponent(
      email
    )}`,
    "GET"
  );
  return data;
}

// Passkey sign-in ceremony. Two calls, same shape as the password flow:
// fetch a challenge, then hand the browser's assertion back for
// verification. See Users::WebauthnSessionsController.
export async function getPasskeyAuthenticationOptions() {
  const { data } = await request(
    "/users/webauthn_authentication/options",
    "POST"
  );
  return data;
}

export async function authenticateWithPasskey(credential) {
  return request("/users/webauthn_authentication", "POST", { credential });
}
